import { ExperimentOutlined } from '@ant-design/icons'
import { Admin } from '@models/Admin'
import { useAuth } from '@modules/auth/contexts/Auth'
import {
  isAnalyticsInterceptorEnabled,
  setAnalyticsInterceptorEnabled,
} from '@services/AnalyticsGraph'
import {
  isInternalAdmin,
  localFlags,
  setPreviewBadgesVisible,
  usePreviewBadgesVisible,
} from '@utils/featureFlags'
import { Popover, Switch, Typography } from 'antd'
import { useState } from 'react'

// Same test as the Gleap initialization in main.tsx
const hasGleapLauncher = Boolean(import.meta.env.VITE_GLEAP_PROJECT_ID)

interface DevSetting {
  key: string
  label: string
  description: string
  enabled: boolean
  onChange: (enabled: boolean) => void
  /** Counted in the red badge while on: a state that alters what is shown */
  alertWhenEnabled: boolean
}

/**
 * Floating per-browser tools. Outside production it carries the developer
 * tools; in production it only appears for internal admins, with the switch
 * hiding the `Preview` tags.
 */
const DevToolsFloatingButton = () => {
  const { currentUser } = useAuth<Admin, {}>()
  const internal = isInternalAdmin(currentUser)
  const previewBadgesVisible = usePreviewBadgesVisible()
  const [interceptorEnabled, setInterceptorEnabled] = useState(
    isAnalyticsInterceptorEnabled()
  )

  const settings: DevSetting[] = [
    ...(localFlags.analyticsInterceptor
      ? [
          {
            key: 'analytics-interceptor',
            label: 'Mock interceptor',
            description: 'Analytics graph & events',
            enabled: interceptorEnabled,
            onChange: (v: boolean) => {
              setAnalyticsInterceptorEnabled(v)
              setInterceptorEnabled(v)
            },
            alertWhenEnabled: true,
          },
        ]
      : []),
    ...(internal
      ? [
          {
            key: 'preview-badges',
            label: 'Preview badges',
            description: 'Mark features not yet enabled for everyone',
            enabled: previewBadgesVisible,
            onChange: setPreviewBadgesVisible,
            alertWhenEnabled: false,
          },
        ]
      : []),
  ]

  if (!localFlags.devTools && !internal) return null
  if (settings.length === 0) return null

  const activeCount = settings.filter(
    (s) => s.alertWhenEnabled && s.enabled
  ).length

  const content = (
    <div style={{ width: 248 }}>
      {settings.map((setting, i) => (
        <div
          key={setting.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '8px 0',
            borderTop: i > 0 ? '1px solid #f0f0f0' : undefined,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Text style={{ fontSize: 13 }}>
              {setting.label}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {setting.description}
            </Typography.Text>
          </div>
          <Switch
            size="small"
            checked={setting.enabled}
            onChange={setting.onChange}
          />
        </div>
      ))}
    </div>
  )

  return (
    <Popover
      content={content}
      title={
        <Typography.Text style={{ fontSize: 11, color: '#888' }}>
          Dev Tools
        </Typography.Text>
      }
      trigger="click"
      placement="topRight"
      arrow={false}
    >
      <div
        style={{
          // Stacked above Gleap's launcher (48px, 20px from the corner) and
          // centered on it, which would otherwise cover this button. Gleap
          // only loads with a project id (main.tsx); without it, the corner.
          position: 'fixed',
          bottom: hasGleapLauncher ? 88 : 20,
          right: hasGleapLauncher ? 24 : 20,
          zIndex: 9999,
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          border: '1px solid #333',
          color: '#e5e5e5',
          fontSize: 18,
        }}
      >
        <ExperimentOutlined />
        {activeCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -3,
              right: -3,
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: '#f87171',
              fontSize: 9,
              fontWeight: 700,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </div>
    </Popover>
  )
}

export default DevToolsFloatingButton
